import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { articles } from "@/data/articles";
import { ArticleData } from "@/lib/types";
import { BookOpen, ExternalLink, ChevronRight, ArrowDown } from "lucide-react";
import RieeLogo from "@/components/RieeLogo";

// ── Animated counter ──────────────────────────────────────────
function AnimatedCount({ target, suffix = "" }: { target: number; suffix?: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const duration = 1200;
          const startTime = performance.now();
          const step = (now: number) => {
            const p = Math.min((now - startTime) / duration, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setCount(Math.floor(eased * target));
            if (p < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target]);

  return (
    <div ref={ref} className="tabular-nums">
      {count}
      {suffix}
    </div>
  );
}

// ── Category config ───────────────────────────────────────────
const categoryConfig: Record<string, { label: string; badge: string; bar: string; accent: string }> = {
  ESTUDIOS: {
    label: "ESTUDIOS",
    badge: "bg-[var(--riee-purple)] text-white",
    bar: "from-[var(--riee-purple)] to-[var(--riee-purple-light)]",
    accent: "var(--riee-purple)",
  },
  REFLEXIONES: {
    label: "REFLEXIONES",
    badge: "bg-[var(--riee-gold)] text-gray-900",
    bar: "from-[var(--riee-gold)] to-[var(--riee-gold-light)]",
    accent: "var(--riee-gold)",
  },
  "RESEÑAS": {
    label: "RESEÑAS",
    badge: "bg-gray-700 text-white",
    bar: "from-gray-500 to-gray-400",
    accent: "#6b7280",
  },
};

// ── Article Card ──────────────────────────────────────────────
function ArticleCard({ article, idx }: { article: ArticleData; idx: number }) {
  const navigate = useNavigate();
  const cc = categoryConfig[article.category] ?? categoryConfig["ESTUDIOS"];
  const ref = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <article
      ref={ref}
      className="card-lift bg-white border border-[var(--border)] rounded-xl overflow-hidden cursor-pointer flex flex-col group"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(32px)",
        transition: `opacity 0.55s ease ${idx * 80}ms, transform 0.55s ease ${idx * 80}ms`,
      }}
      onClick={() => navigate(`/articulo/${article.id}`)}
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && navigate(`/articulo/${article.id}`)}
      role="link"
      aria-label={`Ver artículo: ${article.title}`}
    >
      {/* Animated top accent bar */}
      <div className={`h-1 w-full bg-gradient-to-r ${cc.bar} origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300`} />
      <div className={`h-1 w-full bg-gradient-to-r ${cc.bar} -mt-1`} style={{ opacity: 0.3 }} />

      <div className="p-6 flex flex-col flex-1">
        {/* Category + pages */}
        <div className="flex items-center justify-between mb-4">
          <span className={`text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full ${cc.badge}`}>
            {article.category}
          </span>
          <span className="text-xs font-mono text-gray-400 tabular-nums">
            pp. {article.pagesStart}–{article.pagesEnd}
          </span>
        </div>

        {/* Title */}
        <h2
          className="font-semibold leading-snug mb-3 text-gray-900 group-hover:text-[var(--riee-purple)] transition-colors duration-200"
          style={{ fontFamily: "var(--font-serif)", fontSize: "1.08rem" }}
        >
          {article.title}
        </h2>

        {/* Authors */}
        <p className="text-xs text-gray-500 mb-4 font-mono">
          {article.authors.map((a) => a.name).join("; ")}
        </p>

        {/* Abstract excerpt */}
        <p className="text-sm text-gray-600 leading-relaxed flex-1 line-clamp-4">
          {article.abstract.es}
        </p>

        {/* Key findings mini-row */}
        {article.keyFindings.length > 0 && (
          <div className="flex gap-3 mt-5 pt-4 border-t border-[var(--border)] flex-wrap">
            {article.keyFindings.slice(0, 2).map((kf) => (
              <div key={kf.label} className="flex flex-col">
                <span
                  className="text-base font-bold font-mono leading-none"
                  style={{ color: cc.accent }}
                >
                  {kf.value}
                </span>
                <span className="text-[10px] text-gray-400 uppercase tracking-wide leading-tight">
                  {kf.label}
                </span>
              </div>
            ))}
            <div className="ml-auto flex items-end">
              <span
                className="flex items-center gap-1 text-xs font-semibold transition-colors duration-200"
                style={{ color: cc.accent }}
              >
                <BookOpen size={12} />
                Ver artículo
                <ChevronRight size={12} className="group-hover:translate-x-1 transition-transform duration-200" />
              </span>
            </div>
          </div>
        )}
      </div>
    </article>
  );
}

// ── Section Header ────────────────────────────────────────────
function SectionHeader({ label, count, cc }: {
  label: string;
  count: number;
  cc: typeof categoryConfig[string];
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="flex items-center gap-4 mb-8"
      style={{ opacity: vis ? 1 : 0, transform: vis ? "translateX(0)" : "translateX(-24px)", transition: "opacity 0.5s ease, transform 0.5s ease" }}
    >
      <span className={`px-4 py-1.5 text-xs font-bold rounded-full uppercase tracking-widest ${cc.badge}`}>
        {label}
      </span>
      <div className={`flex-1 h-px bg-gradient-to-r ${cc.bar} opacity-40`} />
      <span className="text-xs text-gray-400 font-mono tabular-nums">{count} artículo{count !== 1 ? "s" : ""}</span>
    </div>
  );
}

// ── Grid pattern background ───────────────────────────────────
function GridBg() {
  return (
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        backgroundImage:
          "linear-gradient(rgba(200,169,81,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(200,169,81,0.04) 1px, transparent 1px)",
        backgroundSize: "48px 48px",
      }}
    />
  );
}

// ── Main Home ─────────────────────────────────────────────────
export default function Home() {
  const estudios = articles.filter((a) => a.category === "ESTUDIOS");
  const reflexiones = articles.filter((a) => a.category === "REFLEXIONES");
  const resenas = articles.filter((a) => a.category === "RESEÑAS");

  const [heroVis, setHeroVis] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setHeroVis(true), 80);
    return () => clearTimeout(t);
  }, []);

  const scrollDown = () => {
    document.getElementById("articulos")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[var(--background)] overflow-x-hidden">
      {/* ── Hero ── */}
      <header
        className="relative min-h-screen flex flex-col justify-center overflow-hidden"
        style={{ background: "linear-gradient(135deg, #0A0F1A 0%, #12082E 55%, #0D1520 100%)" }}
      >
        <GridBg />

        {/* Orbs */}
        <div
          className="absolute -top-24 -right-24 w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{
            background: "radial-gradient(circle, rgba(107,58,142,0.22) 0%, transparent 70%)",
            animation: "pulse-slow 8s ease-in-out infinite",
          }}
        />
        <div
          className="absolute bottom-0 -left-32 w-[400px] h-[400px] rounded-full pointer-events-none"
          style={{
            background: "radial-gradient(circle, rgba(200,169,81,0.12) 0%, transparent 70%)",
            animation: "pulse-slow 10s ease-in-out infinite 2s",
          }}
        />

        <div
          className="relative z-10 max-w-5xl mx-auto px-6 py-20 flex flex-col items-center text-center"
          style={{ opacity: heroVis ? 1 : 0, transform: heroVis ? "translateY(0)" : "translateY(30px)", transition: "opacity 0.8s ease, transform 0.8s ease" }}
        >
          {/* Label strip – ECAE style */}
          <div
            className="flex items-center gap-3 mb-10"
            style={{ opacity: heroVis ? 1 : 0, transition: "opacity 0.8s ease 0.1s" }}
          >
            <div className="h-px w-12 bg-[var(--riee-gold)] opacity-60" />
            <span className="text-[var(--riee-gold)] text-xs font-mono uppercase tracking-[0.2em]">
              Publicación Académica · Vol. 26, Núm. 1 · 2026
            </span>
            <div className="h-px w-12 bg-[var(--riee-gold)] opacity-60" />
          </div>

          {/* RIEE Logo */}
          <div
            className="mb-8"
            style={{ opacity: heroVis ? 1 : 0, transition: "opacity 0.8s ease 0.2s" }}
          >
            <RieeLogo height={52} />
          </div>

          {/* Main title */}
          <h1
            className="text-white text-4xl md:text-6xl font-bold leading-tight mb-5 max-w-4xl"
            style={{
              fontFamily: "var(--font-serif)",
              opacity: heroVis ? 1 : 0,
              transition: "opacity 0.9s ease 0.3s",
              textShadow: "0 2px 40px rgba(107,58,142,0.3)",
            }}
          >
            Revista Internacional de{" "}
            <span style={{ color: "var(--riee-gold)" }}>Estudios</span> en Educación
          </h1>

          {/* Subtitle */}
          <p
            className="text-white/55 text-base max-w-lg mx-auto mb-10 leading-relaxed"
            style={{ opacity: heroVis ? 1 : 0, transition: "opacity 0.9s ease 0.45s" }}
          >
            ISSN 1405-9525 &nbsp;/&nbsp; e-ISSN 2954-3401 · Publicación académica especializada en educación,
            investigación pedagógica y cosmovisión adventista.
          </p>

          {/* Divider ornament */}
          <div
            className="riee-divider my-6 max-w-xs mx-auto w-full"
            style={{ opacity: heroVis ? 1 : 0, transition: "opacity 0.9s ease 0.5s" }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <rect x="6" y="0" width="2" height="14" fill="var(--riee-gold)" opacity="0.7" />
              <rect x="0" y="6" width="14" height="2" fill="var(--riee-gold)" opacity="0.7" />
            </svg>
          </div>

          {/* Stats row */}
          <div
            className="grid grid-cols-3 gap-5 max-w-md mx-auto w-full"
            style={{ opacity: heroVis ? 1 : 0, transition: "opacity 0.9s ease 0.6s" }}
          >
            {[
              { v: 7, l: "Artículos", sfx: "" },
              { v: 4, l: "Países", sfx: "" },
              { v: 2026, l: "Año", sfx: "" },
            ].map((s) => (
              <div key={s.l} className="hero-stat flex flex-col items-center">
                <div className="text-[var(--riee-gold)] font-bold text-3xl font-mono leading-none">
                  <AnimatedCount target={s.v} suffix={s.sfx} />
                </div>
                <div className="text-white/50 text-xs mt-2 uppercase tracking-wider">{s.l}</div>
              </div>
            ))}
          </div>

          {/* Scroll CTA */}
          <button
            onClick={scrollDown}
            className="mt-16 flex flex-col items-center gap-2 text-white/40 hover:text-white/70 transition-colors group"
            aria-label="Ir a los artículos"
          >
            <span className="text-xs font-mono uppercase tracking-widest">EXPLORAR</span>
            <div className="w-px h-8 bg-white/20 group-hover:bg-[var(--riee-gold)] transition-colors" />
            <ArrowDown size={14} className="animate-bounce" />
          </button>
        </div>
      </header>

      {/* ── Articles section ── */}
      <main id="articulos" className="max-w-5xl mx-auto px-6 py-16 space-y-16">

        {/* ESTUDIOS */}
        <section>
          <SectionHeader label="ESTUDIOS" count={estudios.length} cc={categoryConfig["ESTUDIOS"]} />
          <div className="grid gap-6 sm:grid-cols-2">
            {estudios.map((a, i) => <ArticleCard key={a.id} article={a} idx={i} />)}
          </div>
        </section>

        {/* REFLEXIONES */}
        <section>
          <SectionHeader label="REFLEXIONES" count={reflexiones.length} cc={categoryConfig["REFLEXIONES"]} />
          <div className="grid gap-6 sm:grid-cols-2">
            {reflexiones.map((a, i) => <ArticleCard key={a.id} article={a} idx={i} />)}
          </div>
        </section>

        {/* RESEÑAS */}
        <section>
          <SectionHeader label="RESEÑAS" count={resenas.length} cc={categoryConfig["RESEÑAS"]} />
          <div className="grid gap-6 sm:grid-cols-2">
            {resenas.map((a, i) => <ArticleCard key={a.id} article={a} idx={i} />)}
          </div>
        </section>
      </main>

      {/* ── Footer ── */}
      <footer
        className="border-t border-[var(--border)] py-10 text-center mt-8"
        style={{ background: "linear-gradient(0deg, #0A0F1A 0%, transparent 100%)" }}
      >
        <div className="mb-4">
          <RieeLogo height={32} className="mx-auto" />
        </div>
        <a
          href="https://riee.um.edu.mx"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-sm text-[var(--riee-gold)] hover:text-[var(--riee-gold-light)] font-medium transition-colors"
        >
          <ExternalLink size={13} />
          riee.um.edu.mx
        </a>
        <p className="text-xs text-gray-500 mt-2">
          © 2026 Universidad de Montemorelos · RIEE Vol. 26, Núm. 1
        </p>
      </footer>
    </div>
  );
}
